import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Button,
  buttonClasses,
  Card,
  EmptyState,
  Page,
  PageHeader,
  Panel,
  Select,
  StatCard,
  StatCardList,
  Table,
  Toolbar,
} from '@evogard/ui';
import {
  csvCell,
  csvContent,
  formatInstant,
  loadFailureMessage,
  ticketPriorityLabel,
  ticketStatusLabel,
} from '@evogard/domain';
import type {
  ReportAssignee,
  ReportCategoryOpen,
  ReportSlice,
  TicketDashboard,
  TicketReportExport,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { saveCsv } from '../../lib/csv.js';
import { can, useSessionContext } from '../../lib/session.js';
import { count, formatDate } from './common.js';
import {
  TicketFilterButton,
  TicketFilterSummary,
  ticketSearch,
  useTicketFilters,
} from './filters.js';

/**
 * O dashboard do setor, na tela de Relatório (`demandas-47`).
 *
 * ## Por setor, e por quê
 *
 * Até 22/09/2026 esta tela contava o recorte INTEIRO que a pessoa enxerga —
 * os setores dela, a fila comum, o que ela abriu e o que está com ela, tudo
 * somado. A pergunta que o dono fez é outra: "como está o MEU setor". Por
 * isso ela conta um setor por vez, escolhido no seletor do topo entre os da
 * pessoa. Quem tem `tickets.tickets:ver_qualquer` escolhe qualquer setor, e
 * "Todos os setores" é o relatório de antes.
 *
 * Quem escolhe é a tela; quem confere é o servidor — o setor de outra pessoa
 * volta 403, e não uma contagem vazia.
 *
 * ## O que ela mostra
 *
 * Os números do setor, quem está com o quê AGORA (com zero para quem não
 * pegou nada), as categorias do setor abertas e em andamento, e os cortes de
 * `demandas-42`. Tudo sobre o mesmo `where` da mesa: o total daqui é o da mesa
 * filtrada pelo mesmo setor.
 *
 * ## O que continua de `demandas-42`
 *
 * A exportação em CSV e a folha de impressão (`@media print` em `theme.css`).
 * O `GraficoController` do `ticketgard` continua de fora — ano fixo em '2026'
 * dentro do SQL, e contando só os chamados de quem olha.
 */
export function ReportScreen() {
  const { data: context } = useSessionContext();
  const filters = useTicketFilters();
  const [failure, setFailure] = useState<string | null>(null);
  const tenantId = context?.currentTenant.id;
  const tenantName = context?.currentTenant.nome ?? 'base';

  const report = useQuery({
    queryKey: ['demandas', 'relatorio', tenantId, ...filters.key],
    queryFn: async () => {
      const r = await api.tickets.report({ query: filters.query });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o relatório'));
      }
      return r.body;
    },
    // trocar de base não segura a contagem da anterior sob o nome da nova
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const body = report.data;
  const data = body !== undefined && isRead(body) ? body : undefined;
  const noDepartment = body?.estado === 'sem_setor';

  const exportar = useMutation({
    mutationFn: async () => {
      const r = await api.tickets.exportReport({ query: filters.query });
      return ok(r, 200, 'Não foi possível exportar o relatório.');
    },
    onSuccess: (body) => {
      /*
       * O arquivo cortado não sai.
       *
       * `truncated` é o servidor dizendo que o recorte tem mais linhas do que o
       * teto da exportação. Baixar assim entregaria uma planilha que parece
       * completa — e é a primeira pessoa a somar a coluna que descobriria, se
       * descobrisse. Estreitar o período é a saída, e a frase diz isso.
       */
      if (body.truncated) {
        setFailure(
          `O recorte tem ${count(body.total, 'ticket', 'tickets')} e a exportação vai até ${body.rows.length}. ` +
            'Estreite o período — um arquivo cortado que não diz que foi cortado é pior do que nenhum arquivo.',
        );
        return;
      }
      setFailure(null);
      downloadCsv(body, tenantName, data?.department?.nome ?? null);
    },
    onError: (e: Error) => setFailure(e.message),
  });

  /*
   * O setor escolhido mora no endereço, com os outros recortes: recarregar a
   * página ou mandar o link mostra o mesmo setor. O valor vazio só existe para
   * quem vê tudo, e é "Todos os setores".
   */
  const chosenDepartment = data?.department?.id ?? '';
  /* a mesma permissão que o servidor confere em `dashboardScope` */
  const seesAny = can(context, 'tickets.tickets', 'ver_qualquer');
  const seesAll = data !== undefined && data.department === null;

  return (
    <Page measure="cheia">
      <PageHeader
        title="Relatório de tickets"
        actions={
          <div data-print="fora" className="flex flex-wrap gap-2">
            <Link to="/tickets" className={buttonClasses({ variant: 'contorno' })}>
              Voltar à mesa
            </Link>
            {/*
             * Imprimir é o substituto do PDF do legado, e é o navegador que o
             * faz: a folha de `theme.css` põe A4 paisagem, tira a navegação e
             * força a paleta clara.
             */}
            <Button variant="contorno" onClick={() => window.print()} disabled={!data}>
              Imprimir
            </Button>
            <Button
              loading={exportar.isPending}
              disabled={data === undefined || data.total === 0}
              onClick={() => exportar.mutate()}
            >
              Exportar CSV
            </Button>
          </div>
        }
      >
        {data?.department
          ? `Os tickets do setor ${data.department.nome}, em ${tenantName}.`
          : `Os tickets de ${tenantName}, contadas pelo recorte abaixo.`}
      </PageHeader>

      {data &&
        (data.departments.length > 1 || (seesAny && data.departments.length > 0)) && (
          <div data-print="fora" className="max-w-xs">
            <Select
              label="Setor"
              value={chosenDepartment}
              onChange={(e) =>
                filters.apply({
                  ...filters.values,
                  departmentId: e.target.value ? [e.target.value] : [],
                })
              }
            >
              {/* "todos" só para quem vê tudo — para os outros o servidor recusaria */}
              {seesAny && <option value="">Todos os setores</option>}
              {data.departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nome}
                </option>
              ))}
            </Select>
          </div>
        )}

      <div data-print="fora">
        <Toolbar
          search={ticketSearch(filters)}
          toolbar={<TicketFilterButton filters={filters} withoutDepartment />}
        />
      </div>

      {/*
       * O `data-print` vai no `<div>`, e não no `<Alert>`: componente da casa
       * não repassa atributo desconhecido, e a folha impressa sairia com o
       * aviso dentro.
       */}
      {failure && (
        <div data-print="fora">
          <Alert tone="erro">{failure}</Alert>
        </div>
      )}
      {report.isError && <Alert tone="erro">{report.error.message}</Alert>}

      {noDepartment && (
        <EmptyState icon="demandas" title="Você não está em nenhum setor.">
          O relatório conta os tickets de um setor seu. Peça a quem administra a
          plataforma para incluir você no setor em que trabalha.
        </EmptyState>
      )}

      {!noDepartment && (!report.isError || data) && (
        <>
          <Panel
            title={data?.department ? `No setor ${data.department.nome}` : 'No recorte'}
            descricao={<TicketFilterSummary filters={filters} />}
          >
            <StatCardList>
              <StatCard icon="demandas" label="Tickets" value={data?.total} />
              <StatCard
                icon="demandas"
                label="Abertos"
                value={data && sliceTotal(data.byStatus, 'aberta')}
                support="ninguém começou"
              />
              <StatCard
                icon="pessoas"
                label="Em andamento"
                value={data && sliceTotal(data.byStatus, 'em_andamento')}
              />
              <StatCard
                icon="relogio"
                label={ticketStatusLabel('aguardando_solicitante')}
                value={data && sliceTotal(data.byStatus, 'aguardando_solicitante')}
              />
              <StatCard
                icon="relogio"
                label="Vencidos"
                value={data?.overdue}
                /* vermelho só quando o número É a má notícia */
                tone={data?.overdue ? 'erro' : undefined}
                support="em aberto, passadas do prazo"
              />
              <StatCard
                icon="pessoas"
                label="Sem responsável"
                value={data?.withoutAssignee}
                support="em aberto, na fila de ninguém"
              />
              <StatCard
                icon="confirmado"
                label="Concluídas em 30 dias"
                value={data?.completedLast30Days}
              />
              <StatCard
                icon="historico"
                label="Idade média dos abertos"
                value={
                  data === undefined
                    ? undefined
                    : data.openAverageAgeDays === null
                      ? '—'
                      : count(data.openAverageAgeDays, 'dia', 'dias')
                }
                support={
                  data?.openAverageAgeDays === null ? 'nenhum em aberto' : undefined
                }
              />
            </StatCardList>
          </Panel>

          <div className="grid gap-5 lg:grid-cols-2">
            <AssigneeCard people={data?.byAssignee} loading={report.isLoading} />
            <CategoryOpenCard
              categories={data?.byCategoryOpen}
              loading={report.isLoading}
              department={data?.department?.nome ?? null}
            />
            <SliceCard
              title="Por situação"
              slices={data?.byStatus}
              total={data?.total}
              loading={report.isLoading}
              empty="Nenhum ticket no recorte."
            />
            <SliceCard
              title="Por prioridade"
              slices={data?.byPriority}
              total={data?.total}
              loading={report.isLoading}
              empty="Nenhum ticket no recorte."
            />
            {/* por fila só diz alguma coisa quando o recorte tem mais de uma */}
            {seesAll && (
              <SliceCard
                title="Por fila"
                slices={data?.byDepartment}
                total={data?.total}
                loading={report.isLoading}
                empty="Nenhum ticket no recorte."
              />
            )}
          </div>

          {/*
           * Número em documento vem com data, e no papel esta é a única linha
           * que diz quando a folha foi tirada. `generatedAt` é do servidor.
           */}
          {data && (
            <p className="text-xs text-texto-suave">
              {tenantName}
              {data.department ? ` · ${data.department.nome}` : ''} · gerado em{' '}
              {formatInstant(data.generatedAt)}
            </p>
          )}
        </>
      )}
    </Page>
  );
}

function sliceTotal(slices: ReportSlice[], key: string): number {
  return slices.find((s) => s.key === key)?.total ?? 0;
}

/**
 * A barra de proporção ao lado do número — o número é quem informa; a barra
 * só deixa a coluna varrível de relance. Uma cor, sem legenda (é uma série
 * só), e escondida do leitor de tela, que já leu o número.
 */
function Proportion({ value, max }: { value: number; max: number }) {
  const width = max === 0 ? 0 : Math.round((value / max) * 100);
  return (
    <span className="flex items-center gap-2">
      <span className="w-8 text-right tabular-nums">{value}</span>
      <span
        aria-hidden
        className="h-2 w-24 overflow-hidden rounded-full bg-superficie-suave"
      >
        <span
          className="block h-full rounded-full bg-primaria-texto"
          style={{ width: `${width}%` }}
        />
      </span>
    </span>
  );
}

/**
 * Quem está com o quê AGORA — só demanda em aberto.
 *
 * Quem pode aderir no setor e não tem nada aparece com zero: "ninguém está
 * com nada" é resposta, e é a linha que responde "quem pode pegar a próxima".
 */
function AssigneeCard({
  people,
  loading,
}: {
  people: ReportAssignee[] | undefined;
  loading: boolean;
}) {
  const max = Math.max(0, ...(people ?? []).map((p) => p.total));
  return (
    <Card
      title="Com cada colaborador"
      descricao="Tickets em aberto aderidos a cada pessoa do setor, agora."
      bleed
      className="min-w-0"
    >
      <Table
        label="Tickets com cada colaborador"
        items={people ?? []}
        loading={loading}
        rowKey={(p) => p.userId ?? 'sem-responsavel'}
        empty="Ninguém no setor pode aderir a tickets."
        columns={[
          {
            title: 'Colaborador',
            minWidth: '10rem',
            cell: (p) =>
              p.userId === null ? (
                <span className="text-texto-suave">{p.nome}</span>
              ) : (
                p.nome
              ),
          },
          {
            title: 'Abertos',
            cell: (p) => <span className="tabular-nums">{p.open}</span>,
          },
          {
            title: 'Em andamento',
            cell: (p) => <span className="tabular-nums">{p.inProgress}</span>,
          },
          {
            title: 'Aguardando',
            cell: (p) => <span className="tabular-nums">{p.waiting}</span>,
          },
          { title: 'Total', cell: (p) => <Proportion value={p.total} max={max} /> },
        ]}
      />
    </Card>
  );
}

/** As categorias do setor, abertas e em andamento — com as zeradas dentro. */
function CategoryOpenCard({
  categories,
  loading,
  department,
}: {
  categories: ReportCategoryOpen[] | undefined;
  loading: boolean;
  department: string | null;
}) {
  const max = Math.max(0, ...(categories ?? []).map((c) => c.open + c.inProgress));
  return (
    <Card
      title="Categorias em aberto"
      descricao={
        department
          ? `As categorias que ${department} atende, abertas e em andamento.`
          : 'As categorias com ticket aberto ou em andamento.'
      }
      bleed
      className="min-w-0"
    >
      <Table
        label="Categorias em aberto"
        items={categories ?? []}
        loading={loading}
        rowKey={(c) => String(c.categoryId ?? 'sem')}
        empty="Nenhuma categoria com ticket aberto."
        columns={[
          { title: 'Categoria', minWidth: '10rem', cell: (c) => c.nome },
          {
            title: 'Abertos',
            cell: (c) => <span className="tabular-nums">{c.open}</span>,
          },
          {
            title: 'Em andamento',
            cell: (c) => <span className="tabular-nums">{c.inProgress}</span>,
          },
          {
            title: 'Total',
            cell: (c) => <Proportion value={c.open + c.inProgress} max={max} />,
          },
        ]}
      />
    </Card>
  );
}

/**
 * Um corte da contagem.
 *
 * A porcentagem é sobre o total do recorte, e some quando o recorte é vazio:
 * `0 de 0` não é 0%, é uma pergunta sem resposta.
 */
function SliceCard({
  title,
  slices,
  total,
  loading,
  empty,
}: {
  title: string;
  slices: ReportSlice[] | undefined;
  total: number | undefined;
  loading: boolean;
  empty: string;
}) {
  return (
    /*
     * `min-w-0` não é enfeite: item de grid nasce com `min-width: auto`, e a
     * tabela com `minWidth` na primeira coluna fazia a página rolar de lado no
     * telefone. Medido a 390px em 02/09/2026.
     */
    <Card title={title} bleed className="min-w-0">
      <Table
        label={title}
        items={slices ?? []}
        loading={loading}
        rowKey={(l) => l.key}
        empty={empty}
        columns={[
          { title: 'Fatia', minWidth: '10rem', cell: (l) => l.label },
          {
            title: 'Tickets',
            cell: (l) => <span className="tabular-nums">{l.total}</span>,
          },
          {
            title: 'Do recorte',
            cell: (l) => (
              <span className="tabular-nums text-texto-suave">
                {total ? `${Math.round((l.total / total) * 100)}%` : '—'}
              </span>
            ),
          },
        ]}
      />
    </Card>
  );
}

/**
 * O arquivo.
 *
 * As colunas são as nove do `template/pdf.blade.php` do `ticketgard` mais o
 * **título**, o **prazo** e, desde `demandas-44`/`-45`, o **solicitante** e a
 * **placa**. "Aberta por" é quem abriu; "Solicitante" é quem pediu, quando
 * não foi a mesma pessoa.
 *
 * O rodapé de metadados vai depois de uma linha em branco: prosa acima do
 * cabeçalho é o que faz a planilha perder os nomes das colunas.
 */
function downloadCsv(
  data: TicketReportExport,
  tenantName: string,
  department: string | null,
): void {
  const header = [
    'Código',
    'Título',
    'Departamento',
    'Categoria',
    'Aberto por',
    'Solicitante',
    'Placa',
    'Prioridade',
    'Situação',
    'Abertura',
    'Prazo',
    'Fechamento',
    'Responsável',
  ];

  const rows = data.rows.map((l) =>
    [
      l.code,
      l.title,
      /*
       * Vazio sai vazio, e não "—": a planilha é lida por fórmula, e um
       * travessão numa coluna de agrupamento vira uma categoria chamada
       * travessão na primeira tabela dinâmica.
       */
      l.department ?? '',
      l.categoria ?? '',
      l.openedBy,
      l.onBehalfOf ?? '',
      l.placa ?? '',
      ticketPriorityLabel(l.priority),
      ticketStatusLabel(l.situacao),
      formatInstant(l.createdAt),
      l.dueAt ? formatDate(l.dueAt) : '',
      l.completedAt ? formatInstant(l.completedAt) : '',
      l.assignee ?? '',
    ].map(csvCell),
  );

  const content = csvContent([
    header.map(csvCell),
    ...rows,
    [],
    [csvCell('Tickets no recorte'), csvCell(String(data.total))],
    [csvCell('Base'), csvCell(tenantName)],
    ...(department ? [[csvCell('Setor'), csvCell(department)]] : []),
    [csvCell('Gerado em'), csvCell(formatInstant(data.generatedAt))],
  ]);

  saveCsv(content, 'tickets', tenantName);
}

type DashboardRead = Extract<TicketDashboard, { estado: 'lida' }>;

function isRead(body: TicketDashboard): body is DashboardRead {
  return body.estado === 'lida';
}
